import React, { useRef, useEffect, useState } from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import './mapbox-popup-fix.css';
import './map-cursor-mix.css';
import { fetchListings } from './api.js';
import agent from './agent.js';

// Hide the Mapbox popup close button entirely if not already hidden
const hidePopupCloseButton = () => {
  if (!document.querySelector('#mapbox-popup-fix')) {
    const style = document.createElement('style');
    style.id = 'mapbox-popup-fix';
    style.innerHTML = `.mapboxgl-popup-close-button { display: none !important; }`;
    document.head.appendChild(style);
  }
};
hidePopupCloseButton();

// Set the Mapbox token
mapboxgl.accessToken = import.meta.env.VITE_MAPBOX_TOKEN;

// Define filters for the property status
const STATUS_FILTERS = [
  { label: 'All Listings', value: 'all' },
  { label: 'Active Only', value: 'Active' },
  { label: 'Pending', value: 'Pending' },
  { label: 'Closed', value: 'Closed' }
];

export default function SparkMap() {
  const mapContainer = useRef(null);
  const map = useRef(null);
  const [listings, setListings] = useState([]);
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Load listings from Spark API
  useEffect(() => {
    async function loadListings() {
      setLoading(true);
      setError(null);
      try {
        const token = import.meta.env.VITE_SPARK_API_KEY;
        const agentId = agent.sparkAgentId;
        if (!token || !agentId) throw new Error('Missing Spark API key or agent ID');
        
        const data = await fetchListings({ agentId, token });
        setListings(data);
        console.log('Fetched listings for map:', data.length);
      } catch (e) {
        setError(e.message);
        console.error('Fetch error:', e);
      } finally {
        setLoading(false);
      }
    }
    loadListings();
  }, []);

  // Initialize the map
  useEffect(() => {
    if (!mapContainer.current) return;
    console.log('Initializing Spark listings map...');
    
    map.current = new mapboxgl.Map({
      container: mapContainer.current,
      style: 'mapbox://styles/mapbox/streets-v11',
      center: [-121.3153, 44.0582], // Bend, OR coordinates
      zoom: 10
    });
    
    // Add navigation controls
    map.current.addControl(
      new mapboxgl.NavigationControl({ showCompass: true }), 
      'bottom-right'
    );
    
    // Clean up on unmount
    return () => map.current?.remove();
  }, []);

  // Add markers and fit bounds when listings are loaded or filter changes
  useEffect(() => {
    if (!map.current || loading) return;
    
    // Remove existing markers
    if (map.current._markers) {
      map.current._markers.forEach(m => m.remove());
    }
    
    // Active popup tracker
    let activePopup = null;
    
    // Handle click on the map to close any open popup
    map.current.on('click', () => {
      if (activePopup) {
        activePopup.remove();
        activePopup = null;
      }
    });
    
    // Filter listings by status if filter is set
    let filteredListings = listings;
    if (filter !== 'all') {
      filteredListings = listings.filter(listing => listing.status === filter);
    }
    
    console.log(`Showing ${filteredListings.length} listings on map with filter: ${filter}`);
    
    // Create markers for listings with valid coordinates
    const markersWithCoords = filteredListings
      .filter(listing => {
        // Ensure we have valid coordinates
        const lat = listing.raw?.Latitude;
        const lon = listing.raw?.Longitude;
        return lat && lon && !isNaN(lat) && !isNaN(lon);
      })
      .map(listing => {
        const lat = parseFloat(listing.raw.Latitude);
        const lon = parseFloat(listing.raw.Longitude);
        
        // Skip if we have invalid coordinates
        if (isNaN(lat) || isNaN(lon)) return null;
        
        // Determine marker color based on status
        let markerColor = '#003DA5'; // Default blue for RE/MAX
        if (listing.status === 'Pending') markerColor = '#FFC107'; // Yellow
        else if (listing.status === 'Closed') markerColor = '#666666'; // Gray
        else if (listing.status === 'Active') markerColor = '#4CAF50'; // Green
        
        // Get neighborhood and format price
        const neighborhood = listing.SubdivisionName || listing.city || 'N/A';
        const formattedPrice = listing.price ? `$${listing.price.toLocaleString()}` : 'Price unavailable';
        
        // Create the popup HTML content
        const popupHtml = `
          <div style="max-width: 240px; padding: 4px 0;">
            ${listing.photo ? 
              `<div style="margin-bottom: 8px;">
                <img src="${listing.photo}" alt="${neighborhood}" style="width: 100%; height: 120px; object-fit: cover; border-radius: 4px;" />
              </div>` : ''}
            <div style="font-weight: 700; font-size: 15px; margin-bottom: 2px;">${formattedPrice}</div>
            <div style="font-size: 14px; margin-bottom: 4px;">
              ${listing.beds ? `${listing.beds} bd • ` : ''}
              ${listing.baths ? `${listing.baths} ba • ` : ''}
              ${listing.sqft ? `${listing.sqft.toLocaleString()} sq ft` : ''}
            </div>
            <div style="font-size: 13px;">${listing.unparsedAddress || ''}</div>
            <div style="font-size: 13px; color: #555;">${neighborhood}</div>
            <div style="margin-top: 8px; text-align: right;">
              <a href="/" style="color: #003DA5; text-decoration: none; font-weight: 600; font-size: 13px;">View Details</a>
            </div>
          </div>
        `;
        
        // Create the popup but don't attach it to marker yet
        const popup = new mapboxgl.Popup({ 
          offset: 25, 
          closeOnClick: false,
          closeButton: true,
          maxWidth: '300px'
        }).setHTML(popupHtml);
        
        // Handle popup close event to reset activePopup
        popup.on('close', () => {
          if (activePopup === popup) {
            activePopup = null;
          }
        });
        
        // Create and add the marker
        const marker = new mapboxgl.Marker({ color: markerColor, scale: 0.8 })
          .setLngLat([lon, lat]);
        
        // Add click handler to marker
        marker.getElement().addEventListener('click', (e) => {
          e.stopPropagation(); // Prevent map click from immediately closing popup
          
          // Close any existing popup
          if (activePopup) {
            activePopup.remove();
          }
          
          // Open this popup and set as active
          popup.setLngLat([lon, lat]);
          popup.addTo(map.current);
          activePopup = popup;
        });
        
        marker.addTo(map.current);
        return marker;
      })
      .filter(Boolean); // Remove null markers
      
    // Store markers for later cleanup
    map.current._markers = markersWithCoords;
    
    // Fit map to bounds if we have markers
    if (markersWithCoords.length > 0) {
      const bounds = new mapboxgl.LngLatBounds();
      
      filteredListings
        .filter(listing => {
          const lat = listing.raw?.Latitude;
          const lon = listing.raw?.Longitude;
          return lat && lon && !isNaN(lat) && !isNaN(lon);
        })
        .forEach(listing => {
          bounds.extend([
            parseFloat(listing.raw.Longitude), 
            parseFloat(listing.raw.Latitude)
          ]);
        });
      
      map.current.fitBounds(bounds, { 
        padding: 60, 
        maxZoom: 13,
        duration: 1000 
      });
    } else if (filteredListings.length === 0) {
      // If no listings match filter, zoom out to show Bend area
      map.current.flyTo({
        center: [-121.3153, 44.0582], // Bend, OR
        zoom: 10,
        essential: true
      });
    }
  }, [listings, filter, loading]);

  return (
    <div style={{
      position: 'relative',
      display: 'block',
      width: '100%',
      height: '100vh',
      margin: 0,
      padding: 0,
      overflow: 'hidden'
    }}>
      {/* Agent Info */}
      <div style={{
        position: 'absolute',
        top: 24,
        left: 32,
        zIndex: 2,
        color: '#000',
        background: 'rgba(255,255,255,0.9)',
        padding: '8px 16px',
        borderRadius: 8,
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        margin: 0
      }}>
        <div style={{ fontWeight: 700, fontSize: 22, lineHeight: 1.1 }}>{agent.name}</div>
        <div style={{ fontWeight: 400, fontSize: 13, color: '#555', marginTop: 2, letterSpacing: 0.2 }}>{agent.office}</div>
      </div>
      
      {/* Filter Dropdown */}
      <div style={{ position: 'absolute', top: 24, right: 32, zIndex: 2 }}>
        <select
          value={filter}
          onChange={e => setFilter(e.target.value)}
          style={{
            padding: '8px 16px',
            borderRadius: 8,
            fontSize: 16,
            border: '1px solid #ccc',
            background: '#fff',
            color: '#111',
            fontWeight: 600,
            boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
          }}
        >
          {STATUS_FILTERS.map(f => (
            <option key={f.value} value={f.value}>{f.label}</option>
          ))}
        </select>
      </div>
      
      {/* Map Container */}
      <div
        ref={mapContainer}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          margin: 0,
          padding: 0
        }}
      />
      
      {/* Back Button */}
      <div style={{
        position: 'absolute',
        bottom: 24,
        left: 32,
        zIndex: 2
      }}>
        <a
          href="/"
          style={{
            display: 'inline-block',
            background: '#fff',
            padding: '12px 24px',
            borderRadius: 8,
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
            color: '#000',
            textDecoration: 'none',
            fontWeight: 600
          }}
        >
          Back to listings
        </a>
      </div>
      
      {/* Loading Indicator */}
      {loading && (
        <div style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          background: 'rgba(255,255,255,0.95)',
          padding: 32,
          borderRadius: 16,
          zIndex: 10,
          fontSize: 20,
          fontWeight: 600
        }}>Loading Spark listings data...</div>
      )}
      
      {/* Error Message */}
      {error && (
        <div style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          background: 'rgba(255,255,255,0.95)',
          padding: 32,
          borderRadius: 16,
          zIndex: 10,
          fontSize: 20,
          fontWeight: 600,
          color: '#c00'
        }}>
          Error loading listings: {error}
        </div>
      )}
      
      {/* Listing Count */}
      <div style={{
        position: 'absolute',
        bottom: 24,
        right: 32,
        zIndex: 2,
        background: 'rgba(255,255,255,0.9)',
        padding: '8px 16px',
        borderRadius: 8,
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        fontSize: 14,
        fontWeight: 500
      }}>
        {!loading && (
          <>
            Showing {filter === 'all' ? 'all' : filter.toLowerCase()} listings: 
            {' '}
            <strong>
              {filter === 'all' 
                ? listings.length 
                : listings.filter(l => l.status === filter).length}
            </strong>
          </>
        )}
      </div>
    </div>
  );
}
